import { useEffect, useState } from "react";
import type { School } from "../lib/types";

function initials(name: string) {
  const words = name.replace(/^(The |University of )/i, "").split(/[\s-]+/).filter(Boolean);
  return (words[0]?.[0] ?? "") + (words[1]?.[0] ?? "");
}

function domainOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** Favicon/logo sources to try in order for a domain. Google's service returns a
 * consistent 128px icon; DuckDuckGo is the backstop. (Clearbit's logo API was retired.)
 * If all fail we fall through to a colored monogram. */
function logoCandidates(domain: string): string[] {
  return [
    `https://www.google.com/s2/favicons?sz=128&domain=${domain}`,
    `https://icons.duckduckgo.com/ip3/${domain}.ico`,
  ];
}

/** Schools whose favicon is white / near-white and disappears on a light background —
 * render these on a dark chip. */
const NEEDS_DARK_CHIP = new Set(["washington-state-university"]);

/** School logo with a colored-monogram fallback. Uses an explicit logo_url when set,
 * otherwise a favicon lookup on the school's own domain. */
export function SchoolLogo({ school, size = 40 }: { school: School; size?: number }) {
  const bg = school.primary_color || "#334155";
  const style = { width: size, height: size, minWidth: size };

  const domain = domainOf(school.school_website) || domainOf(school.athletics_domain);
  const sources = school.logo_url ? [school.logo_url] : domain ? logoCandidates(domain) : [];

  const [attempt, setAttempt] = useState(0);
  useEffect(() => setAttempt(0), [domain, school.logo_url]);

  const src = sources[attempt];

  if (src) {
    const darkChip = NEEDS_DARK_CHIP.has(school.id);
    return (
      <img
        key={src}
        src={src}
        alt=""
        style={darkChip ? { ...style, padding: Math.round(size * 0.12) } : style}
        loading="lazy"
        decoding="async"
        className={`rounded object-contain ${darkChip ? "bg-slate-800" : ""}`}
        onError={() => setAttempt((a) => a + 1)}
      />
    );
  }
  return (
    <div
      style={{ ...style, backgroundColor: bg }}
      className="flex items-center justify-center rounded text-xs font-bold uppercase text-white"
    >
      {initials(school.name)}
    </div>
  );
}

/** Thin accent bar in the school's primary color. */
export function BrandBar({ school }: { school: School }) {
  if (!school.primary_color) return null;
  return (
    <div
      className="h-1 w-full rounded-full"
      style={{
        background: school.secondary_color
          ? `linear-gradient(90deg, ${school.primary_color}, ${school.secondary_color})`
          : school.primary_color,
      }}
    />
  );
}
